/**
 * ViewSelector - Dropdown-based view switcher with clear active state
 */

import { useState, useRef, useEffect } from 'react';
import type { View } from './types';

interface ViewSelectorProps {
  views: View[];
  templates: View[];
  activeViewId: string | null;
  onViewSelect: (view: View | null) => void;
  onSaveView: () => void;
  onDeleteView?: (view: View) => void;
  hasUnsavedChanges: boolean;
  isLoading?: boolean;
}

export function ViewSelector({
  views,
  templates,
  activeViewId,
  onViewSelect,
  onSaveView,
  onDeleteView,
  hasUnsavedChanges,
  isLoading = false,
}: ViewSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get the active view object
  const activeView = activeViewId
    ? views.find(v => v.id === activeViewId) || templates.find(v => v.id === activeViewId)
    : null;

  const hasViews = views.length > 0 || templates.length > 0;

  return (
    <div className="flex items-center gap-3 px-4 py-2.5 bg-white border-b border-gray-200">
      {/* View Selector Dropdown */}
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`
            flex items-center gap-2 px-3 py-2 rounded-lg border transition-all
            ${activeView
              ? 'bg-blue-50 border-blue-200 text-blue-700'
              : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
            }
          `}
        >
          {/* View icon/indicator */}
          {activeView ? (
            <>
              {activeView.icon ? (
                <span className="text-base">{activeView.icon}</span>
              ) : (
                <span
                  className="w-2.5 h-2.5 rounded-full"
                  style={{ backgroundColor: activeView.color || '#3B82F6' }}
                />
              )}
              <span className="font-medium">{activeView.name}</span>
            </>
          ) : (
            <>
              <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
              <span className="font-medium">All Items</span>
            </>
          )}

          {/* Unsaved indicator */}
          {hasUnsavedChanges && (
            <span className="w-2 h-2 bg-amber-400 rounded-full" title="Unsaved changes" />
          )}

          {/* Dropdown arrow */}
          <svg
            className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Dropdown Menu */}
        {isOpen && (
          <div className="absolute top-full left-0 mt-1 w-72 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-30 max-h-96 overflow-y-auto">
            {/* All Items option */}
            <button
              onClick={() => {
                onViewSelect(null);
                setIsOpen(false);
              }}
              className={`w-full px-3 py-2.5 text-left flex items-center gap-3 transition-colors ${
                activeViewId === null
                  ? 'bg-blue-50 text-blue-700'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
              <div className="flex-1">
                <div className="font-medium">All Items</div>
                <div className="text-xs text-gray-500">Default view with no filters</div>
              </div>
              {activeViewId === null && (
                <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
            </button>

            {/* Saved Views Section */}
            {views.length > 0 && (
              <>
                <div className="px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider border-t border-gray-100 mt-1">
                  Saved Views
                </div>
                {views.map((view) => (
                  <div
                    key={view.id}
                    className={`group flex items-center gap-3 px-3 py-2.5 transition-colors ${
                      activeViewId === view.id
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <button
                      onClick={() => {
                        onViewSelect(view);
                        setIsOpen(false);
                      }}
                      className="flex-1 flex items-center gap-3 text-left"
                    >
                      {/* View icon or color dot */}
                      {view.icon ? (
                        <span className="text-lg">{view.icon}</span>
                      ) : (
                        <span
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: view.color || '#6B7280' }}
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate flex items-center gap-1.5">
                          {view.name}
                          {view.is_default && (
                            <span className="text-xs text-amber-500">Default</span>
                          )}
                        </div>
                        {view.description && (
                          <div className="text-xs text-gray-500 truncate">{view.description}</div>
                        )}
                      </div>
                      {activeViewId === view.id && (
                        <svg className="w-5 h-5 text-blue-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>

                    {/* Delete button (only for private views) */}
                    {onDeleteView && view.visibility === 'private' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteView(view);
                          setIsOpen(false);
                        }}
                        className="p-1 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Delete view"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
              </>
            )}

            {/* Templates Section */}
            {templates.length > 0 && (
              <>
                <div className="px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider border-t border-gray-100 mt-1">
                  Organization Templates
                </div>
                {templates.map((template) => (
                  <button
                    key={template.id}
                    onClick={() => {
                      onViewSelect(template);
                      setIsOpen(false);
                    }}
                    className={`w-full px-3 py-2.5 text-left flex items-center gap-3 transition-colors ${
                      activeViewId === template.id
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {template.icon ? (
                      <span className="text-lg">{template.icon}</span>
                    ) : (
                      <span
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: template.color || '#6B7280' }}
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{template.name}</div>
                      {template.description && (
                        <div className="text-xs text-gray-500 truncate">{template.description}</div>
                      )}
                    </div>
                    {activeViewId === template.id && (
                      <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>
                ))}
              </>
            )}

            {/* Empty state */}
            {!hasViews && (
              <div className="px-3 py-4 text-center text-sm text-gray-500">
                No saved views yet
              </div>
            )}
          </div>
        )}
      </div>

      {/* Divider */}
      <div className="h-6 w-px bg-gray-200" />

      {/* Quick info about current view */}
      {activeView && (
        <div className="text-sm text-gray-500">
          {activeView.filters.length > 0 && (
            <span className="inline-flex items-center gap-1 mr-3">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              {activeView.filters.length} filter{activeView.filters.length !== 1 ? 's' : ''}
            </span>
          )}
          <span className="inline-flex items-center gap-1">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
            </svg>
            {activeView.columns.length} columns
          </span>
        </div>
      )}

      {/* Spacer */}
      <div className="flex-1" />

      {/* Save/Update View Button */}
      <button
        onClick={onSaveView}
        disabled={isLoading}
        className={`
          px-4 py-2 text-sm font-medium rounded-lg flex items-center gap-2 transition-colors
          ${hasUnsavedChanges
            ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm'
            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100 border border-gray-200'
          }
          disabled:opacity-50
        `}
      >
        {hasUnsavedChanges ? (
          <>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
            </svg>
            {activeViewId ? 'Update View' : 'Save as View'}
          </>
        ) : (
          <>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New View
          </>
        )}
      </button>
    </div>
  );
}
